<template>
  <div v-if="loading" class="gs-results-list gs-skeleton" role="status" aria-live="polite">
    <div v-for="i in 2" :key="`skeleton-cat-${i}`" class="gs-category-group">
      <div class="gs-category-header">
        <h2 class="gs-category-title">
          <zm-skeleton-item variant="text" :percent="40" />
        </h2>
      </div>
      <ul class="gs-feature-list">
        <li v-for="j in 2" :key="`skeleton-feature-${i}-${j}`">
          <div class="gs-result-item gs-setting-section gs-feature-layout">
            <div class="gs-feature-layout-base">
              <p class="gs-setting-name">
                <zm-skeleton-item variant="text" :percent="50" />
              </p>
              <p class="gs-setting-description">
                <zm-skeleton-item variant="text" :percent="70" />
              </p>
            </div>
            <div class="gs-feature-layout-status">
              <zm-skeleton-item variant="text" :percent="60" />
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
  <div
    v-else-if="featureList.length > 0"
    class="gs-results-list"
    :aria-label="t('aria.settingsResults', [featureList.length])">
    <template v-for="tabGroup in featureList" :key="tabGroup.tab">
      <div
        v-for="category in tabGroup.categories"
        :key="category.categroyId"
        class="gs-category-group">
        <div class="gs-category-header">
          <h2 class="gs-category-title">
            {{ [tabGroup.tabName, category.categoryName].filter(Boolean).join(' > ') }}
          </h2>
        </div>
        <ul class="gs-feature-list">
          <li v-for="feature in category.features" :key="feature.featureId">
            <a
              class="gs-result-item gs-setting-section gs-feature-layout"
              href="javascript: void(0)"
              @click="onNavigation(feature)">
              <div class="gs-feature-layout-base">
                <p class="gs-setting-name">
                  <zm-highlight-text
                    :key="`${feature.featureName}::${activeQuery}`"
                    :content="feature.featureName || ''"
                    :highlight="highlightKeywords" />
                </p>
                <p v-if="feature.ui?.desc" class="gs-setting-description">
                  <zm-highlight-text
                    :key="`${feature.featureId}-desc::${activeQuery}`"
                    :content="toPlainText(feature.ui.desc)"
                    :highlight="highlightKeywords" />
                </p>
              </div>
              <div class="gs-feature-layout-status" :class="{ 'is-enabled': feature.value }">
                {{ getFeatureStatus(feature) }}
              </div>
            </a>
          </li>
        </ul>
      </div>
    </template>
  </div>
  <div v-else class="gs-empty" role="status">
    <img src="/assets/search-empty.png" width="200" height="200" alt="" />
    <p class="empty-main">{{ $t('searchEmpty') }}</p>
    <p class="empty-sub" v-html="$t('noSettings')"></p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ZmHighlightText, ZmSkeletonItem } from '@zoom/zoom-ui-vue3'
import { axiosInstance } from '../../../shared/api-helper'
import { toHighlightKeywords, toPlainText } from './highlight.js'
import { trackEvent } from '@/shared/event-tracker'
const { t } = useI18n()

const featureList = ref([])
const loading = ref(false)
const activeQuery = ref('')
const highlightKeywords = computed(() => toHighlightKeywords(activeQuery.value))
let abortController = null

const isAborted = (error, signal) =>
  Boolean(signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError')

const cancel = () => {
  abortController?.abort()
  abortController = null
  loading.value = false
}

const search = async (keyword) => {
  abortController?.abort()
  abortController = null

  const nextQuery = keyword?.trim() ?? ''
  if (!nextQuery) {
    activeQuery.value = ''
    featureList.value = []
    loading.value = false
    return 0
  }

  const controller = new AbortController()
  abortController = controller
  const { signal } = controller
  activeQuery.value = nextQuery

  loading.value = true
  try {
    const accountId = window.microsDatas?.accountId || ''
    const result = await axiosInstance.get('/feature/search', {
      params: {
        keyword: nextQuery,
        ruleLevel: 'account',
        objectId: accountId
      },
      signal
    })

    featureList.value = tranferFeatures(result.data?.result?.options) || []

    return result.data?.result?.options?.length || 0
  } catch (error) {
    if (isAborted(error, signal)) return 0
    console.error('Search settings failed:', error)
    featureList.value = []
    return 0
  } finally {
    if (abortController === controller) loading.value = false
  }
}

const tranferFeatures = (options) => {
  if (!options || !Array.isArray(options)) {
    return []
  }

  const validOptions = options.filter((option) => !option.virtualFeature)

  const tabMap = new Map()

  validOptions.forEach((option) => {
    const { tab, tabName, categoryName, categroyId, dataView } = option

    if (!tab) return
    if (!dataView.featureName) return

    if (!tabMap.has(tab)) {
      tabMap.set(tab, {
        tab,
        tabName: tabName || tab,
        categories: new Map()
      })
    }

    const tabData = tabMap.get(tab)

    if (!tabData.categories.has(categroyId)) {
      tabData.categories.set(categroyId, {
        categoryName,
        categroyId,
        features: []
      })
    }

    if (dataView) {
      dataView.url = `/account/setting?tab=${tab}#${dataView.featureId}`
      tabData.categories.get(categroyId).features.push(dataView)
    }
  })

  const result = []
  tabMap.forEach((tabData) => {
    const categories = []
    tabData.categories.forEach((categoryData) => {
      categories.push({
        categoryName: categoryData.categoryName,
        categroyId: categoryData.categroyId,
        features: categoryData.features
      })
    })

    result.push({
      tab: tabData.tab,
      tabName: tabData.tabName,
      categories
    })
  })

  return result
}

const getFeatureStatus = (feature) => {
  if (feature.ui?.type !== 'switch') {
    return ''
  }
  let status = feature.value ? 'ON' : 'OFF'
  if (feature.supportLock && feature.locked?.value) {
    status += ' (Locked)'
  }
  return status
}

const onNavigation = (feature) => {
  trackEvent({
    linkText: `${feature.featureName}`,
    linkUrl: feature.url,
    uiLocation: 'web portal global search - settings'
  })
  setTimeout(() => {
    window.location.href = feature.url
  }, 100)
}

defineExpose({
  search,
  cancel
})
</script>

<style lang="scss">
.gs-category-group {
  &:not(:last-child) {
    margin-bottom: 16px;
    padding-bottom: 8px;
    .gs-feature-list {
      border-bottom: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
    }
  }
}

.gs-category-header {
  padding: 0 8px;

  .gs-category-title {
    text-align: left;
    font-size: 12px;
    line-height: 16px;
    padding-left: 4px;
    color: var(--zoom-color-text-neutral, #686f79);
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 400;
  }
}

.gs-feature-list {
  list-style: none;
  padding: 0 0 8px;
  margin: 8px 0 0 8px;

  li + li {
    margin-top: 8px;
  }
}
.gs-setting-name {
  font-weight: 600;
  color: var(--zoom-color-text-stronger-neutral);
  margin-bottom: 4px;
  margin-top: 0;
  font-size: 14px;
  line-height: 20px;
}
.gs-setting-description {
  color: var(--zoom-color-text-neutral);
  font-size: 14px;
  line-height: 20px;
  margin-top: 4px;
}

.gs-feature-layout {
  align-items: flex-start;
  display: grid;
  grid-template-columns: auto 120px;
  grid-template-areas: 'base status';
  column-gap: 16px;
  line-height: 20px;
  margin-left: -8px;
}
.gs-feature-layout-base {
  grid-area: base;
}
.gs-feature-layout-status {
  grid-area: status;
  text-align: right;
  font-size: 12px;
  color: var(--zoom-color-text-neutral, #686f79);

  &.is-enabled {
    color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
  }
}

.gs-skeleton {
  pointer-events: none;

  .gs-result-item {
    cursor: default;
  }
}
</style>
